import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export function OrderEditDialog({ order, menu, open, onClose, onDone }) {
  const { settings } = useSettings();
  const sym = settings.currency_symbol || "\u20b9";
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (order) setItems((order.items || []).map((it) => ({ menu_item_id: it.menu_item_id, name: it.name, price: it.price, quantity: it.quantity })));
  }, [order]);

  const setQty = (i, q) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, quantity: Math.max(1, Number(q) || 1) } : it)));
  const del = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));
  const addItem = (id) => {
    const m = menu.find((x) => x.id === id);
    if (!m) return;
    setItems((arr) => {
      const ex = arr.findIndex((it) => it.menu_item_id === id);
      if (ex >= 0) return arr.map((it, idx) => (idx === ex ? { ...it, quantity: it.quantity + 1 } : it));
      return [...arr, { menu_item_id: m.id, name: m.name, price: m.price, quantity: 1 }];
    });
  };

  const subtotal = items.reduce((s, it) => s + it.price * it.quantity, 0);

  const save = async () => {
    if (items.length === 0) { toast.error("Order must have at least one item"); return; }
    setSaving(true);
    try {
      await api.put(`/orders/${order.id}/edit`, {
        items: items.map((it) => ({ menu_item_id: it.menu_item_id, quantity: it.quantity })),
        payment_mode: order.payment_mode, notes: order.notes || "",
      });
      toast.success(`Order ${order.order_number} updated`);
      onClose(); onDone && onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to update order"); }
    finally { setSaving(false); }
  };

  const available = (menu || []).filter((m) => !m.hidden);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-brand-bg max-w-lg max-h-[90vh] overflow-y-auto" data-testid="order-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl tracking-tight text-left">Edit Order {order?.order_number}</DialogTitle>
          <DialogDescription className="text-left">Adjust items and quantities before this order is billed. Inventory is re-synced automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            {items.length === 0 && <p className="text-sm text-brand-muted">No items. Add at least one below.</p>}
            {items.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`edit-order-item-${i}`}>
                <span className="col-span-6 text-sm font-medium truncate">{it.name}</span>
                <span className="col-span-2 text-xs text-brand-muted">{money(it.price, sym)}</span>
                <Input type="number" min={1} value={it.quantity} onChange={(e) => setQty(i, e.target.value)} className="border-brand-line col-span-3 h-9" data-testid={`edit-order-qty-${i}`} />
                <button onClick={() => del(i)} data-testid={`edit-order-del-${i}`} className="text-brand-alert col-span-1 flex justify-center"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold">Add Item</Label>
            <Select value="" onValueChange={addItem}>
              <SelectTrigger className="border-brand-line" data-testid="edit-order-add-select"><SelectValue placeholder="Choose a menu item to add" /></SelectTrigger>
              <SelectContent>
                {available.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} — {money(m.price, sym)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-between items-center border-t border-brand-line pt-3">
            <span className="text-sm text-brand-muted">Items subtotal</span>
            <span className="font-serif text-xl text-brand-text" data-testid="edit-order-subtotal">{money(subtotal, sym)}</span>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="save-order-edit" className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm w-full">
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
